/**
 * Runtime v3 — shared primitives for every contract in this folder.
 *
 * THE RULE (program spec §1, ticket #7765): nothing under `runtime-v3/**` may
 * carry a question, an intent per metric, a definition seeded from code, or a
 * branch on request text. Every file here is therefore content-agnostic: it
 * declares SHAPES and closed vocabularies, never business subjects.
 *
 * Nothing in this folder imports `decision-modules`, `decision-agent-runtime`,
 * `semantic-layer` or `metric-registry`, and nothing here reads `node:*`: the
 * package's client-reachable boundary stays intact.
 */

export const RUNTIME_V3_SCHEMA_VERSION = 'runtime-v3.contracts.v1';
export type RuntimeV3SchemaVersion = typeof RUNTIME_V3_SCHEMA_VERSION;

/**
 * The period buckets a dataset revision may declare. Closed so a compiler can
 * validate grain alignment by lookup rather than by parsing a free-text grain.
 */
export const RUNTIME_V3_PERIOD_GRAINS = ['day', 'week', 'month', 'quarter', 'year'] as const;
export type RuntimeV3PeriodGrain = (typeof RUNTIME_V3_PERIOD_GRAINS)[number];

/**
 * Every measured figure travels as an exact decimal string. A float never
 * carries a business number across this boundary (program spec §3,
 * "money in exact decimals"): JSON round-trips and IEEE addition both lose
 * cents, and a lost cent is a fabricated figure.
 */
export type ExactDecimalString = string;

/** Exactly the shape an exact decimal may take: an optional sign, digits, an optional fraction. */
const EXACT_DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * THE ONE READING of an exact decimal as a float.
 *
 * Returns `null` for anything that is not an exact decimal, so the caller must
 * decide what to do about it rather than receive a silent `NaN` that later
 * renders as a figure. Every place that needs the float form of an
 * authoritative decimal goes through here — which is what makes "the float and
 * the exact value cannot disagree" true by construction rather than by
 * discipline.
 */
export function runtimeV3ExactDecimalToNumber(value: ExactDecimalString): number | null {
  if (typeof value !== 'string' || !EXACT_DECIMAL.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** An ISO-8601 instant, server-authored. Relative forms are resolved before they are stored. */
export type IsoInstant = string;

/** An ISO-8601 calendar date (YYYY-MM-DD) naming a period bucket boundary. */
export type IsoDate = string;

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Is this string a REAL calendar date in ISO-8601 form?
 *
 * Shape alone is not enough. Every date in this runtime is compared as a
 * string — effective periods, coverage spans, bucket boundaries — and string
 * comparison against a value that is not a date produces an answer rather than
 * an error: `'2026-07-01' < 'soon'` is `true`, so a declaration dated "soon"
 * is silently never in effect instead of being refused where it was written.
 * So the month and day are round-tripped through the calendar, and
 * `2026-02-31` is refused like any other date that does not exist.
 */
export function isRuntimeV3IsoDate(candidate: unknown): candidate is IsoDate {
  if (typeof candidate !== 'string') return false;
  const parts = ISO_CALENDAR_DATE.exec(candidate);
  if (parts === null) return false;
  const parsed = new Date(`${candidate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === candidate;
}

/** A lowercase hex SHA-256 digest. Content identity, never a name. */
export type ContentDigest = string;

export const RUNTIME_V3_ACTOR_KINDS = ['user', 'service', 'loader'] as const;
export type RuntimeV3ActorKind = (typeof RUNTIME_V3_ACTOR_KINDS)[number];

export const RUNTIME_V3_SCOPE_REF_KINDS = [
  /** Authority over a tenant as a whole. */
  'tenant',
  /** Authority to read one dataset, inside one tenant. */
  'dataset',
  /** Authority to use one definition, in its own scope. */
  'definition',
] as const;
export type RuntimeV3ScopeRefKind = (typeof RUNTIME_V3_SCOPE_REF_KINDS)[number];

/**
 * ONE UNIT OF AUTHORITY — TYPED AND TENANT-BOUND.
 *
 * Deliberately not a string. A scope written as prose can be forged by writing
 * the same prose: an actor in tenant B holding the literal
 * `"dataset:org_positions"` would satisfy a check meant for tenant A. Here the
 * tenant is part of the reference's identity, so a foreign actor's scope can
 * never equal a required one however it is spelled.
 *
 * `tenantId` is `null` only for a shared-scope definition, which belongs to no
 * tenant by construction.
 */
export type RuntimeV3ScopeRef = Readonly<{
  kind: RuntimeV3ScopeRefKind;
  tenantId: string | null;
  objectRef: string;
}>;

/**
 * A comparison key for set membership. Never parsed, never matched against a
 * pattern: it exists only so a `Set` can hold scope references.
 */
export function runtimeV3ScopeRefKey(scopeRef: RuntimeV3ScopeRef): string {
  return JSON.stringify([scopeRef.kind, scopeRef.tenantId, scopeRef.objectRef]);
}

export function runtimeV3ScopeRefEquals(
  left: RuntimeV3ScopeRef,
  right: RuntimeV3ScopeRef,
): boolean {
  return (
    left.kind === right.kind &&
    left.tenantId === right.tenantId &&
    left.objectRef === right.objectRef
  );
}

/**
 * WHO DID IT. Recorded on every lifecycle transition and every accepted run;
 * never defaulted, never inferred from the absence of a caller.
 */
export type RuntimeV3Actor = Readonly<{
  /** Current trusted RBAC, never body fields or persisted original grants. */
  actorPermissions?: readonly string[];
  authorityScope?: import('../ask-helix/contracts/projected-question').ProjectedQuestionAuthorityScopeV1;
  actorId: string;
  actorKind: RuntimeV3ActorKind;
  /** The tenant the actor acted for. Cross-tenant references are refused, not coerced. */
  tenantId: string;
  /**
   * The authority this actor holds NOW, as typed tenant-bound references.
   *
   * This is the CALLER'S authority, not a record of what a plan needed. What a
   * plan needed is derived from its own resolved references at admission and
   * persisted on the run, so a replay is judged against the plan rather than
   * against whatever else the original caller happened to hold.
   */
  scopeRefs: readonly RuntimeV3ScopeRef[];
}>;

/**
 * Closed-set membership as a lookup, not a branch.
 *
 * The engineering standard forbids control flow that branches on string
 * matching. Validation in this folder is therefore expressed as "is this
 * candidate a member of this declared vocabulary" and as record lookups keyed
 * by a closed union — never as a chain of equality tests on literals.
 */
export function isRuntimeV3Member<T extends string>(
  vocabulary: readonly T[],
  candidate: unknown,
): candidate is T {
  return typeof candidate === 'string' && (vocabulary as readonly string[]).includes(candidate);
}

/** A plain JSON object. The registry stores payloads without reading their contents. */
export type RuntimeV3JsonObject = Readonly<Record<string, unknown>>;
